// Story 10.3 — Toolbar con filtros sincronizados a URL.

import { Search } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ConversationFilters as Filters } from '@/lib/query-keys';

interface Props {
  filters: Filters;
  onChange: (next: Filters) => void;
}

/** P-269: debounce de keystrokes del search box. Sin esto, cada tecla
 *  navega → el loader dispara una query con `q ILIKE %X%` (full seq-scan,
 *  ver D-43 sobre índices funcionales). Un usuario tipeando "pizzeria"
 *  generaba 8 round-trips a la DB en menos de un segundo. */
const SEARCH_DEBOUNCE_MS = 300;

export function ConversationFilters({ filters, onChange }: Props): React.ReactElement {
  const idQ = useId();
  const idStatus = useId();
  const idSince = useId();
  const idFlow = useId();

  // Estado local del input de búsqueda; commit debounced al `onChange`.
  const [qLocal, setQLocal] = useState<string>(filters.q ?? '');
  // Sincronizar cuando el filter externo cambia (back nav, deep-link).
  useEffect(() => {
    setQLocal(filters.q ?? '');
  }, [filters.q]);
  // Commit debounced. `filters`/`onChange` se consumen vía closure dentro del
  // setTimeout — el spread `...filters` en el callback evita stale state.
  // biome-ignore lint/correctness/useExhaustiveDependencies: debounce by design
  useEffect(() => {
    const next = qLocal || undefined;
    if (next === filters.q) return;
    const t = window.setTimeout(() => {
      onChange({ ...filters, q: next });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [qLocal]);

  return (
    <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => e.preventDefault()}>
      <div className="min-w-[180px] flex-1">
        <Label htmlFor={idQ} className="text-xs">
          Buscar
        </Label>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            id={idQ}
            type="search"
            value={qLocal}
            placeholder="Nombre o teléfono"
            className="pl-7"
            onChange={(e) => setQLocal(e.target.value)}
          />
        </div>
      </div>
      <div>
        <Label htmlFor={idStatus} className="text-xs">
          Estado
        </Label>
        <select
          id={idStatus}
          value={filters.status ?? ''}
          onChange={(e) =>
            onChange({
              ...filters,
              status: (e.target.value || undefined) as Filters['status'],
            })
          }
          className="h-9 rounded-md border border-border bg-background px-2 text-sm"
        >
          <option value="">Todos</option>
          <option value="active">Activo</option>
          <option value="expired">Expirado</option>
          <option value="completed">Completado</option>
        </select>
      </div>
      <div>
        <Label htmlFor={idSince} className="text-xs">
          Desde
        </Label>
        <select
          id={idSince}
          value={filters.since ?? ''}
          onChange={(e) => onChange({ ...filters, since: e.target.value || undefined })}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm"
        >
          <option value="">Cualquiera</option>
          <option value="today">Hoy</option>
          <option value="7d">Últimos 7 días</option>
          <option value="30d">Últimos 30 días</option>
        </select>
      </div>
      <div className="min-w-[120px]">
        <Label htmlFor={idFlow} className="text-xs">
          Flow
        </Label>
        <Input
          id={idFlow}
          type="text"
          value={filters.flow ?? ''}
          placeholder="todos"
          onChange={(e) => onChange({ ...filters, flow: e.target.value || undefined })}
        />
      </div>
    </form>
  );
}
